import { useEffect } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import Box from '@mui/material/Box';
import Tooltip from '@watershed/ui-core/components/Tooltip';
import Typography from '@mui/material/Typography';

import { Formik } from 'formik';

import { Analytics } from '@watershed/analytics/analyticsUtils';
import LockKeyholeIcon from '@watershed/icons/components/LockKeyhole';
import { switchPinnedOrganizationId } from '@watershed/shared-frontend/utils/pinnedOrganizationId';
import { routeForOrgLogin } from '@watershed/shared-universal/dashboardRoutes';
import { TestIds } from '@watershed/shared-universal/utils/testUtils';
import { Dialog } from '@watershed/ui-core/components/Dialog';
import DialogForm from '@watershed/ui-core/components/DialogForm';
import ErrorBox from '@watershed/ui-core/components/ErrorBox';
import { AutocompleteField } from '@watershed/ui-core/components/Form/AutocompleteField';
import Pill from '@watershed/ui-core/components/Pill';
import { useUserContext } from '../../utils/UserContext';

/**
 * Sorts organizations with the current organization first, then others by name alphabetically,
 * with secondary sorting by organization type (production, staging, demo/test), and finally by ID.
 */
function sortOrganizationsWithCurrentFirst(
  orgs: Array<{
    id: string;
    name: string;
    demoOrg: boolean;
    testOrg: boolean;
    stagingOrg: boolean;
    canAccessInCurrentSession: boolean;
  }>,
  currentOrgId: string
) {
  const currentOrg = orgs.find((org) => org.id === currentOrgId);
  if (!currentOrg) {
    return orgs;
  }

  return [
    currentOrg,
    ...orgs
      .filter((org) => org.id !== currentOrgId)
      .sort((a, b) => {
        // Primary sort by name
        const nameComparison = a.name.localeCompare(b.name, undefined);
        if (nameComparison !== 0) {
          return nameComparison;
        }

        // Secondary sort by type of org: production then staging then demo/test
        const orgTypeOrder = {
          productionOrg: 0,
          stagingOrg: 1,
          demoTestOrg: 2,
        };

        const getOrgTypePriority = (org: typeof a) => {
          if (!org.demoOrg && !org.stagingOrg && !org.testOrg) {
            return orgTypeOrder.productionOrg;
          }
          if (org.stagingOrg) return orgTypeOrder.stagingOrg;
          return orgTypeOrder.demoTestOrg;
        };

        const aPriority = getOrgTypePriority(a);
        const bPriority = getOrgTypePriority(b);

        if (aPriority !== bPriority) {
          return aPriority - bPriority;
        }

        // Finally, sort by ID to ensure consistent ordering
        return a.id.localeCompare(b.id, undefined);
      }),
  ];
}

const ReauthIconAndTooltip = () => {
  const { t } = useLingui();
  return (
    <Tooltip
      title={t({
        message: 'You need to reauthenticate to access this organization',
        context:
          'Tooltip explaining that user needs to log in again to switch to this organization',
      })}
    >
      <LockKeyholeIcon
        sx={{
          color: (theme) => theme.palette.text.secondary,
          fontSize: 16,
          marginRight: 1,
        }}
      />
    </Tooltip>
  );
};

export default function SwitchOrganizationDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useLingui();
  const userContext = useUserContext();

  useEffect(() => {
    if (open) {
      Analytics.modal('switchOrgDialog');
    }
  }, [open]);

  const onCloseWrapped = () => {
    Analytics.action('switchOrgDialog.close');
    onClose();
  };

  const currentOrg = userContext.accessibleOrgs.find(
    (org) => org.id === userContext.orgId
  );

  const orgs = userContext.accessibleOrgs;

  if (!orgs || !currentOrg) {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        header={{
          title: t({
            message: 'Switch organization',
            context:
              'Button copy for users with multiple Organizations in Watershed to switch from one organization to another',
          }),
          subtitle: (
            <ErrorBox level="error" variant="text" sx={{ marginBottom: 2 }}>
              <Trans>
                An error occurred while loading the list of your organizations
              </Trans>
            </ErrorBox>
          ),
        }}
        maxWidth="sm"
      ></Dialog>
    );
  }

  const orderedOrgs = sortOrganizationsWithCurrentFirst(
    orgs,
    userContext.orgId
  );

  return (
    <>
      <Formik
        key={open ? 'open' : 'closed'}
        initialValues={{ name: null, id: null }}
        onSubmit={(value, form) => {
          if (value.id) {
            const selectedOrg = orderedOrgs.find((org) => org.id === value.id);
            if (selectedOrg && !selectedOrg.canAccessInCurrentSession) {
              window.open(routeForOrgLogin(selectedOrg.id));
              onCloseWrapped();
            } else {
              switchPinnedOrganizationId({ orgId: value.id });
              onCloseWrapped();
            }
          } else {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            form.validateField('organization');
          }
        }}
      >
        {(form) => {
          const selectedOrg = orderedOrgs.find(
            (org) => org.id === form.values.id
          );
          const showLockIcon =
            selectedOrg && !selectedOrg.canAccessInCurrentSession;

          return (
            <DialogForm
              open={open}
              onClose={() => onCloseWrapped()}
              maxWidth="sm"
              submit="Switch"
              header={{
                title: t({
                  message: 'Switch organization',
                  context:
                    'Button copy for users with multiple Organizations in Watershed to switch from one organization to another',
                }),
              }}
            >
              <AutocompleteField
                options={orderedOrgs}
                getOptionKey={(option) => option.id}
                getOptionDisabled={(option) => option.id === userContext.orgId}
                textFieldSlotProps={{
                  input: {
                    startAdornment: showLockIcon ? (
                      <ReauthIconAndTooltip />
                    ) : null,
                  },
                }}
                renderOption={(props, org) => {
                  return (
                    <li {...props}>
                      <Box
                        data-testid={TestIds.SwitchOrgOption}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          width: '100%',
                        }}
                      >
                        <Typography variant="body1" sx={{ flex: 1 }}>
                          {org.name}
                        </Typography>

                        <Box
                          sx={{
                            display: 'flex',
                            gap: 0.5,
                            alignItems: 'center',
                          }}
                        >
                          {!org.canAccessInCurrentSession && (
                            <ReauthIconAndTooltip />
                          )}
                          {org.id === userContext.orgId && (
                            <Pill
                              color="primary"
                              size="small"
                              label={
                                <Trans context="Label for organization the user is currently logged in to">
                                  Current
                                </Trans>
                              }
                            />
                          )}
                          {(org.demoOrg || org.testOrg) && (
                            <Pill
                              label={
                                <Trans context="Label for demo organization">
                                  Demo
                                </Trans>
                              }
                              color="decorative"
                              size="small"
                              clickable={false}
                            />
                          )}
                          {org.stagingOrg && (
                            <Pill
                              label={
                                <Trans context="Label for staging organization">
                                  Staging
                                </Trans>
                              }
                              color="warning"
                              size="small"
                              clickable={false}
                            />
                          )}
                        </Box>
                      </Box>
                    </li>
                  );
                }}
                sx={{
                  width: 'auto',
                }}
                label={t({
                  message: 'Which organization would you like to switch to?',
                  context:
                    'Form label for a list of Watershed organizations that a user can switch to',
                })}
                size="medium"
                id="organization"
                required
                onChange={(evt, org) => {
                  // eslint-disable-next-line @typescript-eslint/no-floating-promises
                  form.setFieldValue('id', org?.id);
                }}
              />
            </DialogForm>
          );
        }}
      </Formik>
    </>
  );
}
